import { Injectable } from '@angular/core';
import {
  distinctUntilChanged,
  filter,
  first,
  repeatWhen,
  takeUntil,
} from 'rxjs/operators';
import {
  PubNubService,
  PermissionService,
  UserService,
  convertPubNubTaskToTask,
} from '@app/core/services';
import { Store } from '@ngrx/store';
import { State } from '@app/store';
import { InboxActions } from '@app/store/inbox';
import * as TasksActions from '@app/modules/features/modules/tasks/store/tasks.actions';
import { Channel, PubNub } from '@app/core/models';
import { AccessService } from '@app/core/services/access.service';
import isEqual from 'lodash.isequal';

@Injectable({ providedIn: 'root' })
export class ChannelService {
  constructor(
    private _pubNubService: PubNubService,
    private _userService: UserService,
    private _permissionService: PermissionService,
    private _store: Store<State>,
    private _accessService: AccessService,
  ) {}

  async initialize(): Promise<void> {
    this._initializeInbox();
    this._initializeTaskInbox();
    this._initializeRoomCleanTaskInbox();
    this._pubNubService.isInitialized$.subscribe(() => {
      this._updateInboxOnIncomingMessages();
      this._listenForTaskInboxEvents();
      this._listenForRoomCleanTaskInboxEvents();
    });
  }

  private _initializeInbox(): void {
    this._permissionService.hasMessaging$
      .pipe(
        first((hasPermission) => hasPermission),
        repeatWhen(() => this._userService.newUser$),
      )
      .subscribe(() => {
        this._store.dispatch(InboxActions.initialize());
      });
  }

  private _initializeTaskInbox(): void {
    this._accessService.canAccessGeneralTasks$
      .pipe(
        first((canAccessGeneralTasks) => canAccessGeneralTasks),
        repeatWhen(() => this._userService.newUser$),
      )
      .subscribe((canAccessGeneralTasks) => {
        if (canAccessGeneralTasks) {
          this._store.dispatch(
            TasksActions.channelServiceForTasksInitialized(),
          );
        }
      });
  }

  private _initializeRoomCleanTaskInbox(): void {
    this._accessService.canAccessRoomCleanTasks$
      .pipe(
        first((canAccessRoomCleanTasks) => canAccessRoomCleanTasks),
        repeatWhen(() => this._userService.newUser$),
      )
      .subscribe((canAccessRoomCleanTasks) => {
        if (canAccessRoomCleanTasks) {
          this._store.dispatch(
            TasksActions.channelServiceForRoomCleanTasksInitialized(),
          );
        }
      });
  }

  private _updateInboxOnIncomingMessages(): void {
    this._pubNubService.inboxEvent$
      .pipe(
        takeUntil(
          this._pubNubService.isInitialized$.pipe(
            filter((isInitialized) => !isInitialized),
          ),
        ),
      )
      .subscribe(({ message }) => {
        if (message.type === Channel.Type.MARK_AS_READ) {
          this._store.dispatch(
            InboxActions.markChannelAsReadSuccess({
              channelName: message.name,
            }),
          );
        } else {
          const channel = this._mapPubNubMessageToChannel(message);
          this._store.dispatch(InboxActions.upsertChannel({ channel }));
          const activeChannel = this._pubNubService.activeChatChannel;
          if (message.name === activeChannel) {
            this._store.dispatch(
              InboxActions.markChannelAsRead({ channelName: channel.name }),
            );
          }
        }
      });
  }

  private _listenForTaskInboxEvents(): void {
    this._pubNubService.taskInboxEvent$
      .pipe(
        takeUntil(
          this._pubNubService.isInitialized$.pipe(
            filter((isInitialized) => !isInitialized),
          ),
        ),
      )
      .subscribe((taskMessageEvent) => {
        const taskListItem = convertPubNubTaskToTask(
          taskMessageEvent.message.taskListItem,
        );
        if (taskMessageEvent.message.action === 'TASK_CREATED') {
          this._store.dispatch(
            TasksActions.pubNubTaskCreated({ task: taskListItem }),
          );
        }
        if (taskMessageEvent.message.action === 'TASK_DELETED') {
          this._store.dispatch(
            TasksActions.pubNubTaskDeleted({ task: taskListItem }),
          );
        }
        if (taskMessageEvent.message.action === 'TASK_UPDATED') {
          this._store.dispatch(
            TasksActions.pubNubTaskUpdated({ task: taskListItem }),
          );
        }
      });
  }

  private _listenForRoomCleanTaskInboxEvents(): void {
    this._pubNubService.roomCleanTaskInboxEvent$
      .pipe(
        distinctUntilChanged((previousEvent, currentEvent) =>
          isEqual(previousEvent.message, currentEvent.message),
        ),
        takeUntil(
          this._pubNubService.isInitialized$.pipe(
            filter((isInitialized) => !isInitialized),
          ),
        ),
      )
      .subscribe((messageEvent) => {
        if (messageEvent.message.action === 'ASSIGNEE_WORKING_STATUS_UPDATED') {
          this._store.dispatch(
            TasksActions.pubNubAssigneeWorkingStatusUpdated({
              assignee: messageEvent.message.assignee,
            }),
          );
        } else {
          const taskListItem = convertPubNubTaskToTask(
            messageEvent.message.taskListItem,
          );
          if (messageEvent.message.action === 'ROOM_CLEAN_TASK_CREATED') {
            this._store.dispatch(
              TasksActions.pubNubRoomCleanTaskCreated({ task: taskListItem }),
            );
          }
          if (messageEvent.message.action === 'ROOM_CLEAN_TASK_DELETED') {
            this._store.dispatch(
              TasksActions.pubNubRoomCleanTaskDeleted({ task: taskListItem }),
            );
          }
          if (messageEvent.message.action === 'ROOM_CLEAN_TASK_UPDATED') {
            this._store.dispatch(
              TasksActions.pubNubRoomCleanTaskUpdated({ task: taskListItem }),
            );
          }
        }
      });
  }

  private _mapPubNubMessageToChannel(message: PubNub.Message): Channel.Channel {
    return {
      ...message,
      latestMsg: {
        ...message.latestMsg,
        user:
          message.type === Channel.Type.ANNOUNCEMENT
            ? {
                ...message.latestMsg.user,
                userId: message.latestMsg.user.userID,
                userType: message.latestMsg.user.userType,
              }
            : undefined,
      },
      users: message.users.map((user) => ({
        ...user,
        userId: user.userID,
      })),
      isArchived: false,
    };
  }
}
